// Layer 3 (Transformation): the P&L grid — our five cost categories against
// their Budget, split fixed / variable.
//
// Two derived axes, neither of them seeded:
//
//   * Fixed vs variable is a property of each line's quantity. A line is
//     Fixed when its quantity does not scale with tonnes milled — a day's
//     labour shifts, a day's maintenance consumables, BM01's continuous
//     draw — and Variable when it does (every reagent dose, steel media, and
//     balance-of-plant kWh). `costLineTag` is total by construction: it
//     throws on a label it cannot place, so a new line cannot land untagged
//     and be silently counted on one side.
//
//   * Budget is the same unit rates at BUDGET_TONNES_PER_DAY for the period's
//     day count, read from `budgetCostCategoriesForDays` — the one array the
//     Budget total also sums, so the columns and the total cannot drift.

import { formatMoney } from '@/versions/v1/shared/NsrEngine';
import {
  budgetCostCategoriesForDays,
  budgetRevenueForDay,
  costPeriod,
  daysForPeriod,
  formatSignedMoney,
  type CostCategory,
  type CostCategoryName,
  type CostLineView,
  type CostPeriod,
  type CostPeriodView,
} from './Scene.transform';
import { COST_SEED_PROVENANCE, REAGENT_UNIT_COSTS } from './seed';

export type CostLineTag = 'fixed' | 'variable';

export type CostPLTaggedLine = CostLineView & { tag: CostLineTag };

/** The Fixed set, by line label. Every member's quantity is independent of
 *  tonnage: two labour shifts/day, one maintenance-consumables day, the
 *  WO-441 parts day, and BM01's 21,000 kWh/day continuous draw. */
export const FIXED_COST_LINE_LABELS: readonly string[] = [
  'Day + night shift',
  'Consumables',
  'Consumables — WO-441 parts',
  'BM01 ball mill',
];

/** The Variable set, by line label: every reagent dose scales with tonnes
 *  milled, as do steel media and balance-of-plant kWh. Derived from the seed's
 *  reagent table so a new reagent arrives already tagged. */
export const VARIABLE_COST_LINE_LABELS: readonly string[] = [
  ...REAGENT_UNIT_COSTS.map((r) => r.name),
  'Steel media',
  'Balance of plant',
];

const FIXED_SET = new Set(FIXED_COST_LINE_LABELS);
const VARIABLE_SET = new Set(VARIABLE_COST_LINE_LABELS);

export function costLineTag(label: string): CostLineTag {
  const fixed = FIXED_SET.has(label);
  const variable = VARIABLE_SET.has(label);
  if (fixed === variable) {
    throw new Error(`cost line "${label}" carries ${fixed ? 'two' : 'no'} fixed/variable tag — the split must be total`);
  }
  return fixed ? 'fixed' : 'variable';
}

export type CostPLCell = { budget: number; actual: number; delta: number };

export type CostPLRow = {
  key: string;
  label: string;
  /** The Total cost row closes the grid; the five category rows lead it. */
  isTotal: boolean;
  lines: readonly CostPLTaggedLine[];
  fixed: CostPLCell;
  variable: CostPLCell;
  total: CostPLCell;
};

/** Revenue and margin carry no fixed/variable split — such a split exists in
 *  no seed, and inventing one to fill six cells is exactly the fabrication
 *  this route refuses. They read across the summary cells instead. */
export type CostPLSummaryRow = {
  label: 'Revenue' | 'Margin';
  budget: number;
  actual: number;
  delta: number;
};

export type CostPLView = {
  period: CostPeriod;
  label: string;
  dateRange: string;
  dayCount: number;
  rows: readonly CostPLRow[];
  revenue: CostPLSummaryRow;
  margin: CostPLSummaryRow;
  total: CostPLRow;
  categoryActual: Record<CostCategoryName, number>;
  categoryBudget: Record<CostCategoryName, number>;
  /** When true the Reagents row's variance is extra tonnage at the budget
   *  rate, so its Δ carries the neutral tone, not the cost-row warn. */
  reagentVarianceVolumeDriven: boolean;
  provenance: string;
};

function cell(budget: number, actual: number): CostPLCell {
  return { budget, actual, delta: actual - budget };
}

/** Sum a cell's money over the lines carrying one tag. */
function sumByTag(lines: readonly CostPLTaggedLine[], tag: CostLineTag): number {
  return lines.filter((line) => line.tag === tag).reduce((sum, line) => sum + line.dollars, 0);
}

function tagged(lines: readonly CostLineView[]): CostPLTaggedLine[] {
  return lines.map((line) => ({ ...line, tag: costLineTag(line.label) }));
}

/** One category's row: its lines keep their quantities, its three cells are
 *  the two tag sums and their sum. */
function categoryRow(category: CostCategory, budgetCategory: CostCategory): CostPLRow {
  const actual = tagged(category.lines);
  const budget = tagged(budgetCategory.lines);
  const fixed = cell(sumByTag(budget, 'fixed'), sumByTag(actual, 'fixed'));
  const variable = cell(sumByTag(budget, 'variable'), sumByTag(actual, 'variable'));
  return {
    key: category.category,
    label: category.category,
    isTotal: false,
    lines: actual,
    fixed,
    variable,
    total: cell(fixed.budget + variable.budget, fixed.actual + variable.actual),
  };
}

export function costPL(period: CostPeriod): CostPLView {
  const view: CostPeriodView = costPeriod(period);
  const dayCount = daysForPeriod(period).length;
  const budgetCategories = budgetCostCategoriesForDays(dayCount);

  const rows = view.costCategories.map((category) =>
    categoryRow(category, budgetCategories.find((c) => c.category === category.category)!),
  );

  const total = rows.reduce<CostPLRow>(
    (acc, row) => ({
      key: 'Total cost',
      label: 'Total cost',
      isTotal: true,
      lines: [...acc.lines, ...row.lines],
      fixed: cell(acc.fixed.budget + row.fixed.budget, acc.fixed.actual + row.fixed.actual),
      variable: cell(acc.variable.budget + row.variable.budget, acc.variable.actual + row.variable.actual),
      total: cell(acc.total.budget + row.total.budget, acc.total.actual + row.total.actual),
    }),
    {
      key: 'Total cost',
      label: 'Total cost',
      isTotal: true,
      lines: [],
      fixed: cell(0, 0),
      variable: cell(0, 0),
      total: cell(0, 0),
    },
  );

  const revenueBudget = daysForPeriod(period).reduce((sum, day) => sum + budgetRevenueForDay(day), 0);
  const revenueActual = view.revenueTotal;

  return {
    period,
    label: view.label,
    dateRange: view.dateRange,
    dayCount,
    rows,
    revenue: { label: 'Revenue', budget: revenueBudget, actual: revenueActual, delta: revenueActual - revenueBudget },
    margin: {
      label: 'Margin',
      budget: revenueBudget - total.total.budget,
      actual: revenueActual - total.total.actual,
      delta: revenueActual - total.total.actual - (revenueBudget - total.total.budget),
    },
    total,
    categoryActual: Object.fromEntries(rows.map((row) => [row.key, row.total.actual])) as Record<CostCategoryName, number>,
    categoryBudget: Object.fromEntries(rows.map((row) => [row.key, row.total.budget])) as Record<CostCategoryName, number>,
    reagentVarianceVolumeDriven: view.reagentVarianceVolumeDriven,
    provenance: COST_SEED_PROVENANCE,
  };
}

export function formatCostPLCell(n: number): string {
  return formatMoney(n);
}

export function formatCostPLDelta(n: number): string {
  return formatSignedMoney(n);
}

/** The glyph for a Δ that is exactly zero. Signed money would read `+$0` — a
 *  tone about no quantity — and `—` already means "this concept has no value
 *  here" on the six fixed/variable cells revenue and margin cannot fill. So
 *  zero gets its own mark: the magnitude, unsugared, muted in the cell. */
export const ZERO_DELTA = formatMoney(0);
